import {WebConferencesSlidesRepository} from "@api/repositories/WebConferencesSlidesRepository";
import {FileRepository} from "@api/repositories/FileRepository";
import {CreateSlidesParameters} from "@api/models/interfaces/web-conference/CreateSlidesParameters";
import {Store} from "vuex";
import {FileTypes} from "@api/enums/WebConferenceSlides/FileTypes";
import {WebConferencesSlides} from "@api/models/WebСonferencesSlides";
import {UploadFileResult} from "@share/web-conferences/types/media/UploadFileResult";
import {AxiosRequestConfig} from "axios";
import {RenderingStatus} from "@api/enums/WebConferenceSlides/RenderingStatus";
import i18n from "@service/locale/i18";

export class UploadSlidesService {
    uploadPercentage = 0;
    isUploading = false;

    constructor(
        private readonly presentationsRepository: WebConferencesSlidesRepository,
        private readonly filesRepository: FileRepository,
        private readonly schemaId: string,
        private readonly objectId: string,
        private readonly store: Store<any>
    ) {
    }

    async uploadPresentationFile(file: File): Promise<UploadFileResult> {
        this.isUploading = true;

        const result: UploadFileResult = { type: 'success', message: '' };

        try {
            const slides = await this.create({
                fileName: file.name,
                fileType: FileTypes.Pdf
            });

            await this.upload((slides as WebConferencesSlides).originFileId, file);

            await this.startConvert((slides as WebConferencesSlides).id);

            if (slides) {
                slides.renderingStatus = RenderingStatus.Rendering;
            }

            result.slides = slides;
        }
        catch (e) {
            result.type = 'fail';
            result.message = e.message;
        }
        finally {
            this.isUploading = false;
            this.uploadPercentage = 0;
        }

        return result;
    }

    private async create(fileData: CreateSlidesParameters) {
        try {
            return await this.presentationsRepository.create(this.schemaId, this.objectId, fileData);
        }
        catch (e) {
            this.handleException(e, i18n.t('web-conferences.media-library.upload.errors.slides').toString());
        }
    }

    private async upload(fileId: string, file: File) {
        this.uploadPercentage = 0;
        const config = {
            onUploadProgress: (progressEvent: ProgressEvent) => {
                this.uploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            },
            timeout: 0
        } as AxiosRequestConfig

        try {
            await this.filesRepository.upload({id: fileId}, file, config);
        }
        catch (e) {
            this.store.dispatch("reportError", e);
            this.handleException(e, i18n.t('web-conferences.media-library.upload.errors.upload').toString())
        }
    }

    private async startConvert(id: string) {
        try {
            return await this.presentationsRepository.convert(this.schemaId, this.objectId, id);
        }
        catch (e) {
            this.handleException(e, i18n.t('web-conferences.media-library.upload.errors.converting').toString());
        }
    }

    private handleException(e: any, defaultMessage = '') {
        this.store.dispatch("reportError", e);
        let message = defaultMessage;
        if (e.response && e.response.data && e.response.data.message) {
            message = e.response.data.message;
        }
        throw new Error(message);
    }
}
